import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import {
  ArrowLeft, Headphones, Clock, User, Globe, Monitor, Maximize2,
  Building2, Tag, Users, Calendar, AlertCircle, CheckCircle, XCircle,
} from 'lucide-react';
import TicketComments from './TicketComments';
import CommonToaster from '../Common/CommonToaster';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import UserAvatar from '../ui/UserAvatar';
import { getTicketCreatorName } from './supportIdentity';
import './Support.scss';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const categoryLabel = (c) => {
  const map = { platform_bug: 'Platform Bug', institute_issue: 'Institute Issue', feature_request: 'Feature Request', other: 'Other' };
  return map[c] || c;
};

const statusLabel = (s) => {
  const map = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
  return map[s] || s;
};

const StatusIcon = ({ status, size = 14 }) => {
  const map = { open: AlertCircle, in_progress: Clock, resolved: CheckCircle, closed: XCircle };
  const Icon = map[status] || AlertCircle;
  return <Icon size={size} />;
};

const SupportTicketDetails = ({ isAdmin = false, backPath: backPathProp }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const canManage = isAdmin || ['superadmin', 'admin', 'tenantadmin'].includes(user?.usertype);

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const [newStatus, setNewStatus] = useState('');
  const [assignee, setAssignee] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/support/tickets/${id}`);
        setTicket(data.ticket);
        setComments(data.ticket?.comments || []);
        setNewStatus(data.ticket?.status || '');
        setAssignee(data.ticket?.assignedTo || '');
      } catch {
        setToast({ show: true, message: 'Failed to load ticket.', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [id]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await apiClient.put(`/support/tickets/${id}`, {
        status: newStatus,
        assignedTo: assignee || undefined,
      });
      setTicket((prev) => ({ ...prev, status: newStatus, assignedTo: assignee }));
      setToast({ show: true, message: 'Ticket updated successfully.', type: 'success' });
    } catch {
      setToast({ show: true, message: 'Failed to update ticket.', type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleCommentAdded = (comment) => {
    setComments((prev) => [...prev, comment]);
  };

  const backPath = backPathProp || (isAdmin ? '/sa/support' : '/support/tickets');

  if (loading) {
    return (
      <div className="td-page td-page--loading">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="td-page">
        <div className="sp-empty">
          <Headphones size={48} strokeWidth={1.2} />
          <h3>Ticket not found</h3>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeft size={16} /> Back to Tickets
          </Button>
        </div>
      </div>
    );
  }

  const reporterName = getTicketCreatorName(ticket);

  return (
    <div className="td-page">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Top bar */}
      <div className="td-topbar">
        <button className="td-topbar__back" onClick={() => navigate(backPath)}>
          <ArrowLeft size={16} /> Back to Tickets
        </button>
        <div className="td-topbar__badges">
          <span className="td-topbar__id">{ticket.ticketId}</span>
          <span className={`status-badge status-badge--${ticket.status}`}>
            <StatusIcon status={ticket.status} size={13} />
            {statusLabel(ticket.status)}
          </span>
          <span className={`priority-badge priority-badge--${ticket.priority}`}>{ticket.priority} Priority</span>
        </div>
      </div>

      <div className="td-layout">
        {/* Main content column */}
        <div className="td-layout__main">
          {/* Ticket card */}
          <div className="td-card">
            <h2 className="td-card__title">{ticket.title}</h2>
            <p className="td-card__description">{ticket.description}</p>

            {ticket.screenshotUrl && (
              <div className="td-card__attachment">
                <p className="td-card__attachment-label">Attachment</p>
                <a href={ticket.screenshotUrl} target="_blank" rel="noopener noreferrer">
                  <img src={ticket.screenshotUrl} alt="Ticket attachment" />
                </a>
              </div>
            )}

            {canManage ? (
              <div className="td-card__reporter">
                <UserAvatar name={reporterName} size="sm" />
                <div className="td-card__reporter-info">
                  <span className="td-card__reporter-name">{reporterName}</span>
                  {ticket.createdByEmail && (
                    <span className="td-card__reporter-email">{ticket.createdByEmail}</span>
                  )}
                </div>
                <span className="td-card__reporter-date">
                  <Calendar size={12} /> {formatDate(ticket.createdAt)}
                </span>
              </div>
            ) : (
              <div className="td-card__meta">
                <span className="td-card__meta-item">
                  <Calendar size={12} /> {formatDate(ticket.createdAt)}
                </span>
                <span className="td-card__meta-item">
                  <Tag size={12} /> {categoryLabel(ticket.category)}
                </span>
              </div>
            )}
          </div>

          {/* Comments */}
          <TicketComments
            ticketId={id}
            comments={comments}
            onCommentAdded={handleCommentAdded}
            allowInternal={canManage}
          />
        </div>

        {/* Sidebar */}
        <div className="td-layout__sidebar">
          {canManage ? (
            <>
              {/* Manage Ticket card — admin only */}
              <div className="td-manage-card">
                <div className="td-sidebar-card__header">Manage Ticket</div>
                <div className="td-manage-card__body">
                  <div className="td-manage-card__field">
                    <label>Status</label>
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className="td-manage-card__field">
                    <label>Assign To</label>
                    <input
                      type="text"
                      placeholder="Name or email..."
                      value={assignee}
                      onChange={(e) => setAssignee(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100"
                    onClick={handleUpdate}
                    disabled={updating || (newStatus === ticket.status && assignee === (ticket.assignedTo || ''))}
                  >
                    {updating ? <Spinner size="sm" animation="border" /> : 'Update Ticket'}
                  </Button>
                </div>
              </div>

              {/* Details card — admin view */}
              <div className="td-sidebar-card">
                <div className="td-sidebar-card__header">Details</div>
                <div className="td-sidebar-card__body">
                  <div className="td-detail-item">
                    <User size={14} />
                    <div className="td-detail-item__content">
                      <span className="td-detail-item__label">Reporter</span>
                      <span className="td-detail-item__value">{reporterName}</span>
                      {ticket.createdByEmail && (
                        <span className="td-detail-item__sub">{ticket.createdByEmail}</span>
                      )}
                    </div>
                  </div>
                  {ticket.batchName && (
                    <div className="td-detail-item">
                      <Users size={14} />
                      <div className="td-detail-item__content">
                        <span className="td-detail-item__label">Batch</span>
                        <span className="td-detail-item__value">{ticket.batchName}</span>
                      </div>
                    </div>
                  )}
                  <div className="td-detail-item">
                    <Tag size={14} />
                    <div className="td-detail-item__content">
                      <span className="td-detail-item__label">Category</span>
                      <span className="td-detail-item__value">{categoryLabel(ticket.category)}</span>
                    </div>
                  </div>
                  <div className="td-detail-item">
                    <Calendar size={14} />
                    <div className="td-detail-item__content">
                      <span className="td-detail-item__label">Created</span>
                      <span className="td-detail-item__value">{formatDate(ticket.createdAt)}</span>
                    </div>
                  </div>
                  <div className="td-detail-item">
                    <Clock size={14} />
                    <div className="td-detail-item__content">
                      <span className="td-detail-item__label">Last Updated</span>
                      <span className="td-detail-item__value">{formatDate(ticket.updatedAt)}</span>
                    </div>
                  </div>
                  {ticket.assignedTo && (
                    <div className="td-detail-item">
                      <User size={14} />
                      <div className="td-detail-item__content">
                        <span className="td-detail-item__label">Assigned To</span>
                        <span className="td-detail-item__value">{ticket.assignedTo}</span>
                      </div>
                    </div>
                  )}
                  {isAdmin && ticket.tenantId && (
                    <div className="td-detail-item">
                      <Building2 size={14} />
                      <div className="td-detail-item__content">
                        <span className="td-detail-item__label">Institute</span>
                        <span className="td-detail-item__value">{ticket.tenantId?.name || ticket.tenantName || String(ticket.tenantId)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Environment card — admin only */}
              {(ticket.pageUrl || ticket.browser || ticket.screenResolution) && (
                <div className="td-sidebar-card">
                  <div className="td-sidebar-card__header">Environment</div>
                  <div className="td-sidebar-card__body">
                    {ticket.pageUrl && (
                      <div className="td-detail-item">
                        <Globe size={14} />
                        <div className="td-detail-item__content">
                          <span className="td-detail-item__label">Page URL</span>
                          <span className="td-detail-item__value td-detail-item__value--mono">{ticket.pageUrl}</span>
                        </div>
                      </div>
                    )}
                    {ticket.browser && (
                      <div className="td-detail-item">
                        <Monitor size={14} />
                        <div className="td-detail-item__content">
                          <span className="td-detail-item__label">Browser</span>
                          <span className="td-detail-item__value">
                            {ticket.browser?.match(/(?:Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[0] || ticket.browser}
                          </span>
                        </div>
                      </div>
                    )}
                    {ticket.screenResolution && (
                      <div className="td-detail-item">
                        <Maximize2 size={14} />
                        <div className="td-detail-item__content">
                          <span className="td-detail-item__label">Resolution</span>
                          <span className="td-detail-item__value">{ticket.screenResolution}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Ticket Info card — student/instructor view (simplified) */
            <div className="td-sidebar-card">
              <div className="td-sidebar-card__header">Ticket Info</div>
              <div className="td-sidebar-card__body td-ticket-info">
                <div className="td-ticket-info__row">
                  <span className="td-ticket-info__label">Status</span>
                  <span className={`status-badge status-badge--${ticket.status}`}>
                    <StatusIcon status={ticket.status} size={12} />
                    {statusLabel(ticket.status)}
                  </span>
                </div>
                <hr className="td-ticket-info__sep" />
                <div className="td-ticket-info__row">
                  <span className="td-ticket-info__label">Priority</span>
                  <span className={`priority-badge priority-badge--${ticket.priority}`}>
                    {ticket.priority}
                  </span>
                </div>
                <hr className="td-ticket-info__sep" />
                <div className="td-ticket-info__row">
                  <span className="td-ticket-info__label">Category</span>
                  <span className="td-ticket-info__value">{categoryLabel(ticket.category)}</span>
                </div>
                {ticket.batchName && (
                  <>
                    <hr className="td-ticket-info__sep" />
                    <div className="td-ticket-info__row">
                      <span className="td-ticket-info__label">Batch</span>
                      <span className="td-ticket-info__value">{ticket.batchName}</span>
                    </div>
                  </>
                )}
                <hr className="td-ticket-info__sep" />
                <div className="td-ticket-info__row">
                  <span className="td-ticket-info__label">Created</span>
                  <span className="td-ticket-info__value td-ticket-info__value--sm">{formatDate(ticket.createdAt)}</span>
                </div>
                <hr className="td-ticket-info__sep" />
                <div className="td-ticket-info__row">
                  <span className="td-ticket-info__label">Updated</span>
                  <span className="td-ticket-info__value td-ticket-info__value--sm">{formatDate(ticket.updatedAt)}</span>
                </div>
                {ticket.assignedTo && (
                  <>
                    <hr className="td-ticket-info__sep" />
                    <div className="td-ticket-info__row">
                      <span className="td-ticket-info__label">Assigned</span>
                      <span className="td-ticket-info__value">{ticket.assignedTo}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetails;
